<template lang="pug">
.menu-item(v-bind="attrs")
  .menu-link
    rogue-link(:to="url" :categoryId="categoryId" :aria-label="`Link to ${title}`")
      //- Decorative: the name is right next to it, so alt text would make screen readers read it twice
      StaticImage.menu-img(v-if="image" :src="image" alt="" :width="128" ssr lazy)
      | {{title}}
    .arrow
    .after-link
  .panel(v-if="$slots.default")
    slot
</template>
<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  url: string,
  categoryId?: number,
  title: string,
  image?: string,
}>()

const attrs = computed(() => {
  if (props.url === '/deals') {
    return { class: 'deals' }
  }

  return undefined
})
</script>
<style lang="scss" scoped>
.panel {
  cursor: auto;
  display: none;
}

.menu-link {
  position: relative;

  a {
    line-height: $line-height-m;
    display: block;
    text-align: left;
    font-size: $size-xs;
    color: $color-black;
    text-decoration: none;
    padding: $space-s;
    border-bottom: 1px solid $color-gray-light;
  }

  .menu-img {
    display: none;
  }

  .arrow {
    position: absolute;
    display: flex;
    right: $space-xs;
    width: $space-s;
    top: 0;
    height: 100%;
  }
}

@include not-desktop() {
  .menu-link > .icon :deep(svg) {
    fill: #000 !important;
    pointer-events: none;
  }

  .menu-item {
    cursor: pointer;
  }

  .menu-item.hide {
    display: none;
  }

  .menu-link > a {
    pointer-events: none;
  }

  .menu-item.active > .menu-link {
    background-color: $color-gray;
    justify-content: center;
    font-weight: $weight-bold;
    text-align: center;

    > a {
      text-decoration: underline;
      display: inline-block;
      pointer-events: all;
    }
  }

  .menu-item.active > .menu-link .icon {
    left: $space-s;
    transform: rotate(180deg);
  }

  .menu-item.active-child {
    > .panel {
      display: block;
    }

    > .menu-link {
      display: none;
    }
  }

  .menu-item.active {
    > .panel {
      display: block;

      .menu-item.hide {
        display: block;
      }
    }
  }
}

@include all-desktop() {
  .menu-link .arrow {
    display: none;
    height: 40px;
  }

  .top-level > .panel {
    position: absolute;
    left: 100%;
    top: 0;
    height: 100%;
    box-shadow: 0 4px 5px -2px rgba(0, 0, 0, 0.2);
  }

  .top-level > .menu-link {
    min-width: 220px;

    a {
      line-height: 40px;
      white-space: nowrap;
      border-bottom: none;
      pointer-events: all;
      padding: 0 $space-xl 0 $space-s;
    }

    .after-link {
      position: absolute;
      right: -30px;
      width: 70px;
      height: 70px;
      top: -15px;
      display: none;
      overflow: hidden;
      pointer-events: none;
      z-index: 1;

      // Menu aim - creates a pointer blocker to prevent accidentally switching sub menu
      &:before {
        content: '';
        position: absolute;
        top: -50%;
        left: 0;
        pointer-events: all;
        width: 100%;
        height: 100%;
        transform-origin: 0 100%;
        transform: rotate(45deg);
      }
    }
  }

  .menu-link .menu-img {
    margin-bottom: $space-xs;
    display: block;
    box-shadow: 0 0 10px 1px #A2A2A2;
  }

  .top-level:hover {
    > .menu-link {
      > a {
        background-color: $color-black;
        color: $color-white;
      }

      .arrow, .after-link {
        display: flex;
      }
    }

    .panel {
      display: block;
    }
  }
}

</style>
